import { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { motion } from 'framer-motion';
import {
  User,
  Mail,
  Phone,
  Building2,
  MapPin,
  Save,
  X,
  Eye,
  EyeOff,
  Shield,
  Users
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import Button from '@components/common/Button';
import Loading from '@components/common/Loading';
import { Badge } from '@components/common/StatusBadge';
import { apiHelper } from '@services/api';
import { useAuth } from '@context/AuthContext';
import { USER_ROLES, INDIAN_STATES } from '@utils/constants';
import { validateEmail, validatePhone } from '@utils/validators';

const UserForm = ({
  user = null,
  onSubmit,
  onCancel,
  loading = false,
  mode = 'create' // 'create' | 'edit'
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { user: currentUser, hasPermission } = useAuth();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    control,
    formState: { errors },
    reset
  } = useForm({
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      role: USER_ROLES.USER,
      department: '',
      branch_id: '',
      address: '',
      city: '',
      state: '',
      country: 'India',
      pincode: '',
      is_active: true,
      password: '',
      confirm_password: ''
    }
  });

  const watchPassword = watch('password');

  // Fetch branches for dropdown
  const { data: branches = [] } = useQuery({
    queryKey: ['branches'],
    queryFn: async () => {
      const response = await apiHelper.get('/branches');
      return response.data;
    }
  });

  // Reset form when user changes
  useEffect(() => {
    if (user && mode === 'edit') {
      reset({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        phone: user.phone || '',
        role: user.role || USER_ROLES.EMPLOYEE,
        department: user.department || '',
        branch_id: user.branch_id || '',
        address: user.address || '',
        city: user.city || '',
        state: user.state || '',
        country: user.country || 'India',
        pincode: user.pincode || '',
        is_active: user.is_active ?? true,
        password: '',
        confirm_password: ''
      });
    } else if (mode === 'create') {
      reset({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        role: USER_ROLES.USER,
        department: '',
        branch_id: '',
        address: '',
        city: '',
        state: '',
        country: 'India',
        pincode: '',
        is_active: true,
        password: '',
        confirm_password: ''
      });
    }
  }, [user, mode, reset]);

  const handleFormSubmit = async (data) => {
    // Remove password fields if editing and passwords are empty
    if (mode === 'edit' && !data.password) {
      delete data.password;
      delete data.confirm_password;
    }

    try {
      await onSubmit(data);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save user');
    }
  };

  const canEditRole = hasPermission('manage_users') && currentUser?.role === USER_ROLES.ADMIN;
  const requirePassword = mode === 'create';

  return (
    <motion.div
      className="bg-white rounded-xl shadow-sm border"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="p-6 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-3">
          <User className="w-5 h-5 text-primary-600" />
          {mode === 'create' ? 'Add New User' : 'Edit User'}
        </h2>
        <p className="text-sm text-gray-600 mt-1">
          {mode === 'create'
            ? 'Create a new user account with appropriate permissions'
            : 'Update user information and settings'
          }
        </p>
      </div>

      <form onSubmit={handleSubmit(handleFormSubmit)} className="p-6">
        <div className="space-y-8">
          {/* Basic Information */}
          <div>
            <h3 className="text-md font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Users className="w-4 h-4" />
                            Basic Information
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* First Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    First Name <span className="text-error-500">*</span>
                </label>
                <input
                  type="text"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${errors.first_name ? 'border-error-300' : 'border-gray-300'
                  }`}
                  placeholder="Enter first name"
                  {...register('first_name', {
                    required: 'First name is required',
                    minLength: {
                      value: 2,
                      message: 'First name must be at least 2 characters'
                    }
                  })}
                />
                {errors.first_name && (
                  <p className="mt-1 text-sm text-error-600">{errors.first_name.message}</p>
                )}
              </div>

              {/* Last Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Last Name <span className="text-error-500">*</span>
                </label>
                <input
                  type="text"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${errors.last_name ? 'border-error-300' : 'border-gray-300'
                  }`}
                  placeholder="Enter last name"
                  {...register('last_name', {
                    required: 'Last name is required',
                    minLength: {
                      value: 2,
                      message: 'Last name must be at least 2 characters'
                    }
                  })}
                />
                {errors.last_name && (
                  <p className="mt-1 text-sm text-error-600">{errors.last_name.message}</p>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Email Address <span className="text-error-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="w-4 h-4 text-gray-400" />
                  </div>
                  <input
                    type="email"
                    className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${errors.email ? 'border-error-300' : 'border-gray-300'
                    }`}
                    placeholder="Enter email address"
                    {...register('email', {
                      required: 'Email is required',
                      validate: validateEmail
                    })}
                  />
                </div>
                {errors.email && (
                  <p className="mt-1 text-sm text-error-600">{errors.email.message}</p>
                )}
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Phone Number
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Phone className="w-4 h-4 text-gray-400" />
                  </div>
                  <input
                    type="tel"
                    className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${errors.phone ? 'border-error-300' : 'border-gray-300'
                    }`}
                    placeholder="Enter phone number"
                    {...register('phone', {
                      validate: (value) => !value || validatePhone(value)
                    })}
                  />
                </div>
                {errors.phone && (
                  <p className="mt-1 text-sm text-error-600">{errors.phone.message}</p>
                )}
              </div>
            </div>
          </div>

          {/* Role and Department */}
          <div>
            <h3 className="text-md font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Shield className="w-4 h-4" />
                            Role & Department
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Role */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Role <span className="text-error-500">*</span>
                </label>
                <select
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${errors.role ? 'border-error-300' : 'border-gray-300'
                  }`}
                  disabled={!canEditRole}
                  {...register('role', { required: 'Role is required' })}
                >
                  {Object.entries(USER_ROLES).map(([key, value]) => (
                    <option key={key} value={value}>
                      {value.charAt(0).toUpperCase() + value.slice(1)}
                    </option>
                  ))}
                </select>
                {errors.role && (
                  <p className="mt-1 text-sm text-error-600">{errors.role.message}</p>
                )}
                {!canEditRole && (
                  <p className="mt-1 text-xs text-gray-500">You don't have permission to change roles</p>
                )}
              </div>

              {/* Department */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Department
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  placeholder="Enter department"
                  {...register('department')}
                />
              </div>

              {/* Branch */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Branch
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  {...register('branch_id')}
                >
                  <option value="">Select Branch</option>
                  {branches.map((branch) => (
                    <option key={branch.id} value={branch.id}>
                      {branch.name} - {branch.city}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Password Section (for create mode or if editing passwords) */}
          {(mode === 'create' || mode === 'edit') && (
            <div>
              <h3 className="text-md font-semibold text-gray-900 mb-4">
                {mode === 'create' ? 'Account Security' : 'Change Password'}
              </h3>

              {mode === 'edit' && (
                <p className="text-sm text-gray-600 mb-4">
                                    Leave password fields empty to keep current password unchanged.
                </p>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Password {requirePassword && <span className="text-error-500">*</span>}
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      className={`w-full pr-10 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${errors.password ? 'border-error-300' : 'border-gray-300'
                      }`}
                      placeholder="Enter password"
                      {...register('password', {
                        required: requirePassword ? 'Password is required' : false,
                        minLength: {
                          value: 6,
                          message: 'Password must be at least 6 characters'
                        }
                      })}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="w-4 h-4 text-gray-400" />
                      ) : (
                        <Eye className="w-4 h-4 text-gray-400" />
                      )}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="mt-1 text-sm text-error-600">{errors.password.message}</p>
                  )}
                </div>

                {/* Confirm Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Confirm Password {requirePassword && <span className="text-error-500">*</span>}
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      className={`w-full pr-10 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${errors.confirm_password ? 'border-error-300' : 'border-gray-300'
                      }`}
                      placeholder="Confirm password"
                      {...register('confirm_password', {
                        required: requirePassword ? 'Please confirm your password' : false,
                        validate: (value) => {
                          if (requirePassword && value !== watchPassword) {
                            return 'Passwords do not match';
                          }
                          if (!requirePassword && watchPassword && value !== watchPassword) {
                            return 'Passwords do not match';
                          }
                          return true;
                        }
                      })}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="w-4 h-4 text-gray-400" />
                      ) : (
                        <Eye className="w-4 h-4 text-gray-400" />
                      )}
                    </button>
                  </div>
                  {errors.confirm_password && (
                    <p className="mt-1 text-sm text-error-600">{errors.confirm_password.message}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Address Information */}
          <div>
            <h3 className="text-md font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <MapPin className="w-4 h-4" />
                            Address Information
            </h3>

            <div className="space-y-4">
              {/* Address */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Address
                </label>
                <textarea
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors resize-none"
                  placeholder="Enter full address"
                  {...register('address')}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                {/* City */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                                        City
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                    placeholder="Enter city"
                    {...register('city')}
                  />
                </div>

                {/* State */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                                        State
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                    {...register('state')}
                  >
                    <option value="">Select State</option>
                    {INDIAN_STATES.map((state) => (
                      <option key={state} value={state}>
                        {state}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Country */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Country
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors bg-gray-50"
                    value="India"
                    readOnly
                    {...register('country')}
                  />
                </div>

                {/* Pincode */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Pincode
                  </label>
                  <input
                    type="text"
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${errors.pincode ? 'border-error-300' : 'border-gray-300'
                    }`}
                    placeholder="Enter pincode"
                    {...register('pincode', {
                      pattern: {
                        value: /^\d{6}$/,
                        message: 'Pincode must be 6 digits'
                      }
                    })}
                  />
                  {errors.pincode && (
                    <p className="mt-1 text-sm text-error-600">{errors.pincode.message}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Status */}
          <div>
            <label className="flex items-center space-x-3">
              <Controller
                name="is_active"
                control={control}
                render={({ field }) => (
                  <input
                    type="checkbox"
                    checked={field.value}
                    onChange={field.onChange}
                    className="w-4 h-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                )}
              />
              <span className="text-sm font-medium text-gray-700">
                                Active User
              </span>
            </label>
            <p className="mt-1 text-sm text-gray-500">
                            Inactive users cannot login to the system
            </p>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-4 pt-8 border-t border-gray-200 mt-8">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            leftIcon={<X className="w-4 h-4" />}
            disabled={loading}
          >
                        Cancel
          </Button>
          <Button
            type="submit"
            disabled={loading}
            leftIcon={loading ? <Loading size="sm" /> : <Save className="w-4 h-4" />}
          >
            {loading ? 'Saving...' : (mode === 'create' ? 'Create User' : 'Update User')}
          </Button>
        </div>
      </form>
    </motion.div>
  );
};

export default UserForm;